import React from "react";
import { Page, Text, View, Document, StyleSheet, Image } from "@react-pdf/renderer";
import { DQC_COLORS, KSAB_COLOR_MAPPING } from "../../../../utils/constants";
import { formatDuration } from "../../../../utils/utils";
import { TARGET_AGE_MAP } from "../../../../utils/META/cat_109";
import { Svg, Circle, Text as SvgText } from "@react-pdf/renderer";
import PoweredByDQ from '../../../../assets/images/PoweredByDQ_Default.png';

const CircleLetterIcon = ({ letter }) => (
    <Svg width="9" height="9" viewBox="0 0 16 16">
        <Circle cx={8} cy={8} r={7} stroke="#444" strokeWidth={1} fill="none" />
        <SvgText x={8} y={11} fontSize={9} textAnchor="middle" fill="#444">{letter}</SvgText>
    </Svg>
);

const styles = StyleSheet.create({
    page: { paddingTop: 100, paddingBottom: 72, paddingLeft: 54, paddingRight: 70, fontSize: 10, fontFamily: "Helvetica" },

    // Header / Footer
    header: {
        position: "absolute", top: 60, left: 32, right: 36, height:65,
        flexDirection: "row", justifyContent: "flex-end", alignItems: "center",
    },
    headerLogo: { height: '45px', marginLeft: 6, marginTop: 16 , marginBottom: '80px',},

    footer: {
        position: "absolute", bottom: 50, left: 32, right: 32,
        flexDirection: "row", justifyContent: "center", alignItems: "center",
    },
    footerRow: { flexDirection: "row", alignItems: "center", justifyContent: "center", flexWrap: "wrap" },
    footerCopy: { fontSize: 8, color: "#444" },
    footerSpacer: { width: 4 },
    footerPage: { fontSize: 8, color: "#444" },

    // Title block
    subTitleTop: { fontSize: 13, textAlign: "center",marginTop: '30px', marginBottom: 2, },
    title: { fontSize: 18, marginTop: '4px', marginBottom: '4px', fontFamily: "Helvetica-Bold", textAlign: "center" },
    subtitle: { fontSize: 13, textAlign: "center", marginTop: 2, fontStyle: "italic" },

    metaRow: { marginTop: 10, marginBottom: 8 },
    metaLine: { fontSize: 12, textAlign: "left", marginBottom: 2 },
    h2: { fontSize: 14, fontFamily: "Helvetica-Bold", marginTop: '12px', marginBottom: 4 },
    h3: { fontSize: 13, fontFamily: "Helvetica-Bold", marginTop: 6, marginBottom: 3 },

    durationModule:  { fontSize: 9, color: "#000" },
    durationUnit:    { fontSize: 9, color: "#000" },
    durationSession: { fontSize: 9, color: "#000" },

    divider: { height: 1, backgroundColor: "#000", marginVertical: 8 },
    moduleDivider: {
        height: 0.8,
        backgroundColor: "#000",
        width: "100%",
        alignSelf: "center",
        marginVertical: 10,
        borderRadius: 1,
    },
    unitDivider: {
        height: 0.6,
        backgroundColor: "#d9d9d9",
        width: "77%",
        alignSelf: "center",    
        marginVertical: 12,
        borderRadius: 1,
    },

    // KSAB
    ksabRow: { flexDirection: "row", flexWrap: "wrap", alignItems: "center", marginTop: '13px', marginBottom: '13px' },
    ksabDot: { width: 9, height: 9, borderRadius: 5, marginRight: 4 },
    ksabLabel: { fontSize: 9, fontWeight: "bold", marginRight: 10 },

    ksabPill: {
        alignSelf: "center",
        backgroundColor: "#f3f3f3",
        borderRadius: 12,
        paddingVertical: 18,
        paddingHorizontal: 13,
        marginTop: '13px',
        marginBottom: '15px',

        // layout
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
    },
    ksabItem: {
        flexDirection: "row",
        alignItems: "center",
        marginRight: 14,   // spacing between items
    },
    ksabDotLg: {        // slightly larger than before for visibility
        width: 11,
        height: 11,
        borderRadius: 5,
        marginRight: 6,
    },
    ksabLabelBold: { fontSize: 11, fontFamily: "Helvetica-Bold", marginRight: 10 },

    // DQ code chips
    tagWrap: { flexDirection: "row", flexWrap: "wrap", marginTop: 4, marginBottom: 4 },
    tag: {
        backgroundColor: "#e6f1fe",
        borderRadius: 14,
        paddingHorizontal: 8,
        paddingVertical: 5,
        marginRight: 6,
        marginBottom: 6,
        maxWidth: 220,
    },
    tagText: { fontSize: 6, color: "#fff", fontFamily: "Helvetica-Bold", textAlign: "center" },

    // Hierarchy / indentation
    moduleBlock: { marginBottom: 12 },
    unitBlock: {
        marginTop: 8,
        marginLeft: 10,              // first-level indent (Unit)
        paddingLeft: 8,
        borderLeftWidth: 0,
    },
    unitTitleBand: {
        borderRadius: 4,
        paddingVertical: 4,
        paddingHorizontal: 6,
        alignSelf: "flex-start",
        marginBottom: 4,
    },
    unitTitleText: { fontSize: 14, paddingHorizontal: 6, fontFamily: "Helvetica-Bold" },

    sessionBlock: {
        marginTop: '14px',
        marginLeft: '26px',             // deeper indent (Session)
        paddingLeft: '18px',
        paddingRight: '18px',
        borderLeftWidth: 0,
        borderLeftColor: "#e0d6a9",
    },
    sessionHeader: { fontSize: 14, fontFamily: "Helvetica-Bold" },
    sessionMeta: { marginTop: 2, marginBottom: 3,fontSize:'12px' },
    bullet: { fontSize: 12, marginLeft: '10px', marginBottom: 3 },

    legend: { marginTop: 20, paddingTop: 10, borderTopWidth: 1, borderTopColor: "#cccccc" },
    legendTitle: { fontSize: 12, fontFamily: "Helvetica-Bold", marginBottom: 8 },
});

const getDqcoColor = (dqco, dqcoList) => {
    if(!dqcoList) return '#444';
    const obj = dqcoList.find(item => item.dqco_no === dqco);
    if(!obj) return '#444';
    return DQC_COLORS[obj.dqc_no] || '#444';
};
const getDqcoName = (dqco, dqcoList) => {
    if(!dqcoList) return '';
    const obj = dqcoList.find(item => item.dqco_no === dqco);
    return obj?.short_name || obj?.full_name || '';
};
const ksabDisplayOrder = ["Knowledge", "Skills", "Attitudes", "Behaviours"];

const OutlinePdfDoc = ({ programData, programAudience, programDuration, modules = [], isEducatorView, dqcoList }) => {
    const totalModules = modules.length;
    const totalUnits = modules.reduce((acc, m) => acc + (m?.units?.length || 0), 0);
    const totalSessions = modules.reduce(
        (acc, m) => acc + (m?.units || []).reduce((a, u) => a + (u?.lessons?.length || 0), 0),
        0
    );
    const learnersText = Array.isArray(programAudience)
        ? programAudience.map((v) => TARGET_AGE_MAP[v]?.label || v).join(", ")
        : TARGET_AGE_MAP[programAudience]?.label || programAudience || "Not specified";

    return (
        <Document>
            <Page style={styles.page} wrap>
                {/* ===== Fixed Header ===== */}
                <View style={styles.header} fixed>
                    {PoweredByDQ && (
                        <Image style={styles.headerLogo} src={PoweredByDQ} />
                    )}
                </View>

                {/* ===== Title block ===== */}
                <Text style={styles.subTitleTop}>Program Outline for</Text>
                <Text style={styles.title}>{programData?.full_name}</Text>
                <Text style={{ fontSize: 13, textAlign: "center", marginTop: 2, marginBottom:'8px', fontStyle: 'italic'}}>
                    ({isEducatorView ? "Educator View" : "Learner View"})
                </Text>

                {/* Meta */}
                <View style={styles.metaRow}>
                    <Text style={styles.metaLine}><Text style={{ fontFamily: "Helvetica-Bold", fontSize: 12 }}>Learners: </Text>{learnersText}</Text>
                    <Text style={styles.metaLine}>
                        <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 12 }}>Program Structure: </Text>
                        {totalModules} Module(s) / {totalUnits} Unit(s) / {totalSessions} Session(s)
                    </Text>
                    <Text style={styles.metaLine}>
                        <Text style={{ fontFamily: "Helvetica-Bold", fontSize: 12 }}>Program Length: </Text>{formatDuration(programDuration)}
                    </Text>
                </View>

                {/* Overview */}
                {programData?.description && (
                    <>
                        <Text style={styles.h2}>Overview:</Text>
                        <Text style={{fontSize: 12 }}>{programData.description}</Text>
                    </>
                )}

                <View style={styles.ksabPill} wrap={false}>
                    <Text style={styles.ksabLabelBold}>KSAB:</Text>

                    {ksabDisplayOrder.map((k, i) => (
                        <View key={k} style={styles.ksabItem} wrap={false}>
                            <View style={[styles.ksabDotLg, { backgroundColor: KSAB_COLOR_MAPPING[k] || "#bbb" }]} />
                            <Text style={{ fontSize: 9 }}>{k}</Text>
                        </View>
                    ))}
                </View>


                {/*<View style={styles.divider} />*/}

                {/* ===== Modules / Units / Sessions with indentation ===== */}
                {modules.map((mod, mIdx) => (
                    <View key={mIdx} style={styles.moduleBlock}>
                        <View style={styles.moduleDivider} />
                        <Text style={styles.h2}>
                            <Text>Module {mIdx + 1}: {mod.module_name} </Text>
                            <Text style={styles.durationModule}>({formatDuration(mod.module_duration)})</Text>
                        </Text>
                        {(mod.module_educator_description || mod.module_learner_description) && (
                            <Text style={{marginBottom:'10px', fontSize:'12px'}}>
                                {isEducatorView ? mod.module_educator_description : mod.module_learner_description}
                            </Text>
                        )}

                        {/* DQ Code chips */}
                        {mod.unique_dqco?.length ? (
                            <View style={{ marginTop: 6 }}>
                                <Text style={{ fontSize: 11, fontStyle:'italic', fontFamily: "Helvetica-Bold", marginBottom: 2, marginTop: 2 }}>DQ Code(s):</Text>
                                <View style={styles.tagWrap} wrap={false}>
                                    {mod.unique_dqco.map((code, i) => {
                                        const name = getDqcoName(code, dqcoList);
                                        return (
                                            <View key={`${code}-${i}`} style={[styles.tag, {backgroundColor: getDqcoColor(code, dqcoList)}]} wrap={false}>
                                                <Text style={styles.tagText}>{name ? `${code}\n${name}` : code}</Text>
                                            </View>
                                        );
                                    })}
                                </View>
                            </View>
                        ) : null}

                        {/* Units */}
                        {mod.units?.map((u, uIdx) => (
                            <View key={uIdx} style={styles.unitBlock}>
                                <View style={styles.unitDivider} />
                                <View style={styles.unitTitleBand}>
                                    <Text style={styles.unitTitleText}>
                                        Unit {uIdx + 1}: {u.unit_title} <Text style={styles.durationUnit}>({formatDuration(u.unit_duration)})</Text>
                                    </Text>
                                </View>
                                {(u.unit_educator_description || u.unit_learner_description) && (
                                    <Text style={{ paddingLeft: '13px', fontSize:'12px'}}>{isEducatorView ? u.unit_educator_description : u.unit_learner_description}</Text>
                                )}

                                {/* Sessions */}
                                {u.lessons?.map((s, sIdx) => {
                                    const ksabTokens = s.session_unique_ksab || [];
                                    const objectiveItems =
                                        (s.session_learning_objectives || [])
                                            .map(
                                                (o) =>
                                                    o.description_for_educator ||
                                                    o.description_for_learner ||
                                                    o.description
                                            )
                                            .filter(Boolean);

                                    const sessionDescription = isEducatorView
                                        ? s.lesson_educator_description
                                        : s.lesson_learner_description;

                                    return (
                                        <View key={sIdx} style={styles.sessionBlock}>
                                            <Text style={styles.sessionHeader}>
                                                Session {sIdx + 1}: {s.lesson_title} <Text style={styles.durationSession}>({formatDuration(s.lesson_duration)})</Text>
                                            </Text>

                                            {sessionDescription ? (
                                                <Text style={styles.sessionMeta}>{sessionDescription}</Text>
                                            ) : null}

                                            {!!ksabTokens.length && (
                                                <View style={styles.ksabRow} wrap={false}>
                                                    <Text style={{ fontSize: 11, fontWeight: "bold", marginRight: 6 }}>KSAB:</Text>
                                                    {ksabTokens.map((t, i) => (
                                                        <View key={`${t}-${i}`} style={{ flexDirection: "row", alignItems: "center", marginRight: 1 }}>
                                                            <View style={[styles.ksabDot, { backgroundColor: KSAB_COLOR_MAPPING[t] || "#bbb" }]} />
                                                        </View>
                                                    ))}
                                                </View>
                                            )}

                                            {!!objectiveItems.length && (
                                                <View style={{ marginTop: 2 }}>
                                                    <Text style={{ fontSize: 13, fontFamily: "Helvetica-Bold", marginBottom: 2 }}>
                                                        Session Learning Objectives:
                                                    </Text>
                                                    {objectiveItems.map((line, idx) => (
                                                        <View key={idx} style={{ flexDirection: "row",marginLeft:'20px', marginBottom: 6 }}>
                                                            <Text style={{ marginRight: 8, fontSize: 25, fontWeight: "bold", }}>•</Text>
                                                            <Text style={styles.bullet}>{line}</Text>
                                                        </View>
                                                    ))}
                                                </View>
                                            )}
                                        </View>
                                    );
                                })}

                            </View>
                        ))}
                    </View>
                ))}

                <View style={styles.footer} fixed>
                    <View style={styles.footerRow}>
                        <CircleLetterIcon letter="C" />
                        <Text style={styles.footerCopy}>
                            {new Date().getFullYear()} DQ LAB Pte Ltd. All rights reserved. Powered by DQ
                        </Text>
                        <CircleLetterIcon letter="R" />
                        <View style={styles.footerSpacer} />
                        <Text style={styles.footerCopy}>is a registered trademark of DQ LAB Pte Ltd.</Text>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

export default OutlinePdfDoc;
